declare const require: {
  context(
    directory: string,
    useSubdirectories?: boolean,
    regExp?: RegExp
  ): {
    (key: string): string;
    keys(): string[];
  };
};

// Limit context strictly to svg files in one folder
const iconsContext = require.context('../assets/images', false, /^[./a-zA-Z0-9_-]+\.svg$/);

/**
 * Safely retrieves the path of an icon from a given icon path.
 * It ensures that the icon name is valid and matches the files in the context.
 *
 * @param {string} iconPath - The path to the icon, which may include a hash.
 * @returns {string} - The resolved path to the icon or the original path if not found.
 */
export const getIconPath = (iconPath: string): string => {
  // Securely extract base icon name — e.g., "icon-name"
  const baseMatch = iconPath.match(/([a-zA-Z0-9_-]+)(?:\.[a-f0-9]{8,})?\.svg$/);
  if (!baseMatch) return iconPath;

  const iconName = baseMatch[1];

  // Sanitize icon name (not strictly required, but added for defense-in-depth)
  if (!/^[a-zA-Z0-9_-]+$/.test(iconName)) return iconPath;

  const files = iconsContext.keys();

  // Match filenames like './icon-name.svg' or './icon-name.hash.svg'
  const safeRegex = new RegExp(`^\\.\\/${iconName}(?:[.-][a-zA-Z0-9]+)?\\.svg$`);

  const match = files.find((file) => safeRegex.test(file));
  return match ? iconsContext(match) : iconPath;
};

/**
 * Extracts the Bootstrap icon class name from an icon path or icon string.
 * Converts icon paths like "icon-name.svg" or "bi-icon-name" to "bi-icon-name"
 *
 * @param {string} iconPath - The icon path or icon name
 * @returns {string} - The Bootstrap icon class name (e.g., "bi-speedometer2")
 */
export const getIconClassName = (iconPath: string): string => {
  if (!iconPath) return "";

  // If already in bi- format, return as is
  if (iconPath.startsWith("bi-")) {
    return iconPath;
  }

  // Extract icon name from SVG path (e.g., "icon-name.svg" or "icon-name.hash.svg")
  const baseMatch = iconPath.match(/([a-zA-Z0-9_-]+)(?:\.[a-f0-9]{8,})?\.svg$/);
  if (baseMatch) {
    const iconName = baseMatch[1];
    // Convert to Bootstrap icon format
    return `bi-${iconName}`;
  }

  // If it's just a name without extension, prefix with bi-
  if (/^[a-zA-Z0-9_-]+$/.test(iconPath)) {
    return iconPath.startsWith("bi-") ? iconPath : `bi-${iconPath}`;
  }

  return iconPath;
};
